// src/forms/html_element_helpers/custom_fields/fields_schema.js
//
// Parser + strict validator for the `data-hub-form-fields` attribute (base64 UTF-8 JSON).
// Outer array = rows; inner array = fields in that row. Group children are a flat field
// array (no nested rows, no nested groups). Validation is all-or-nothing: any violation
// reports through error_function and returns null so the caller falls back to the legacy
// `data-hub-form-user-fields` renderer — the GUI is the intended author and partial/silent
// rendering would hide bugs.
//
// Enums live here, colocated with the parser (FORM_CHROME_ENUMS-in-form_styling.js
// precedent). They are renderer-only — no static_vars Section-2 / shared-functions sync.

import { parse_base64_json_utf8 } from '../../../helpers';
import { white_list_incoming_person_fields } from '../../../helpers/static_vars';

export const FORM_FIELDS_ENUMS = {
    field_types: ['display_text', 'text', 'email', 'tel', 'checkbox', 'hidden', 'group', 'submit', 'sms_optin'],
    widths: ['1/4', '1/3', '1/2', '2/3', '3/4', 'full'],
    data_targets: ['person', 'custom_data', 'metadata'],
    display_text_tags: ['p', 'h1', 'h2', 'h3'],
    group_variants: ['inner_card'],
};

// Field types that render a named <input> and therefore route somewhere on POST.
const NAMED_INPUT_TYPES = ['text', 'email', 'tel', 'checkbox', 'hidden'];

// Keys become BigQuery / Iterable field names — keep them identifier-shaped.
const FIELD_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

// Channel-level subscription fields are not part of the hosted form contract.
// Generated hosted forms use list IDs and message type IDs only; stale or
// hand-authored field JSON should fail closed instead of posting channel IDs.
export const DISALLOWED_ITERABLE_CHANNEL_FIELD_NAMES = [
    'subscribedChannelIds',
    'unsubscribedChannelIds',
];

// Mirrors the element's static `action_object_data` blob (data_hub_html_element.js).
// A data_target:"custom_data" field with one of these names would collide with (and be
// overridden by / override) the element's own custom_data keys at submit-merge time.
export const RESERVED_CUSTOM_DATA_KEYS = [
    'source', 'form_id', 'form_title', 'form_description', 'form_image_url', 'success_settings',
    'actblue_donation_amount', 'metadata', 'page_url', 'iterableCampaignId', 'iterableTemplateId',
    'iterable_subscribe_list_ids', 'iterable_unsubscribe_list_ids', 'iterable_unsubscribe_message_types',
    'iterable_subscribe_message_types',
];

// Stamped automatically into the merged metadata at submit time (must stay in sync with
// the data-hub-action backend's copy). An authored data_target:"metadata" field with one
// of these names would collide with the auto-stamped keys.
export const RESERVED_METADATA_KEYS = ['form_id', 'data_hub_form_id', 'form_source'];

const is_plain_object = (value) =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const is_primitive = (value) =>
    ['string', 'number', 'boolean'].includes(typeof value);

const is_non_empty_string = (value) =>
    typeof value === 'string' && value.trim() !== '';

/**
 * Normalize a `required` value to boolean. Accepts boolean or 'true'/'false' strings
 * (same leniency as render_input_field). Returns null for anything else (= invalid).
 */
const normalize_required = (value) => {
    if (value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string' && ['true', 'false'].includes(value.toLowerCase())) {
        return value.toLowerCase() === 'true';
    }
    return null;
};

/**
 * Validate + normalize a single field object. Returns the normalized field or throws
 * a string detail message (caught by parse_and_validate_form_fields).
 * @param {Object} field
 * @param {boolean} inside_group
 */
const validate_field = (field, inside_group) => {
    if (!is_plain_object(field)) {
        throw 'field must be a plain object, got ' + JSON.stringify(field);
    }
    const { type } = field;
    if (!FORM_FIELDS_ENUMS.field_types.includes(type)) {
        throw `unknown field type "${type}"`;
    }
    if (field.width !== undefined && !FORM_FIELDS_ENUMS.widths.includes(field.width)) {
        throw `invalid width "${field.width}" on type "${type}"`;
    }

    const normalized = { type, width: field.width || 'full' };

    if (NAMED_INPUT_TYPES.includes(type)) {
        if (!is_non_empty_string(field.name) || !FIELD_NAME_REGEX.test(field.name)) {
            throw `invalid name "${field.name}" on type "${type}" (must match ${FIELD_NAME_REGEX})`;
        }
        normalized.name = field.name;

        if (DISALLOWED_ITERABLE_CHANNEL_FIELD_NAMES.includes(normalized.name)) {
            throw `"${normalized.name}" is not allowed in hosted forms — use Iterable list IDs or message type IDs instead`;
        }

        if (field.data_target !== undefined && !FORM_FIELDS_ENUMS.data_targets.includes(field.data_target)) {
            throw `invalid data_target "${field.data_target}" on field "${field.name}"`;
        }
        normalized.data_target = field.data_target || 'person';

        // Conflicting-combo guard: the person collection loop picks up EVERY named input,
        // so a person-whitelisted name routed to metadata/custom_data would land in BOTH
        // user_data and the declared target — silently mutating the owning org's person
        // record. Use a prefixed name (e.g. partner_sms_optin) instead.
        if (normalized.data_target !== 'person' && white_list_incoming_person_fields.includes(normalized.name)) {
            throw `"${normalized.name}" is a person-whitelisted field name and cannot use data_target "${normalized.data_target}" — rename it (e.g. "partner_${normalized.name}")`;
        }
        if (normalized.data_target === 'custom_data' && RESERVED_CUSTOM_DATA_KEYS.includes(normalized.name)) {
            throw `"${normalized.name}" is a reserved custom_data key`;
        }
        if (normalized.data_target === 'metadata' && RESERVED_METADATA_KEYS.includes(normalized.name)) {
            throw `"${normalized.name}" is a reserved metadata key (stamped automatically at submit)`;
        }

        const required = normalize_required(field.required);
        if (required === null) {
            throw `invalid required value ${JSON.stringify(field.required)} on field "${field.name}"`;
        }
        normalized.required = required;
    }

    switch (type) {
        case 'display_text':
            if (!is_non_empty_string(field.text)) {
                throw 'display_text requires a non-empty "text"';
            }
            if (!FORM_FIELDS_ENUMS.display_text_tags.includes(field.tag)) {
                throw `invalid display_text tag "${field.tag}"`;
            }
            normalized.text = field.text;
            normalized.tag = field.tag;
            break;
        case 'text':
        case 'email':
        case 'tel':
            if (field.label !== undefined) {
                if (typeof field.label !== 'string') throw `label must be a string on field "${field.name}"`;
                normalized.label = field.label;
            }
            if (field.placeholder !== undefined) {
                if (typeof field.placeholder !== 'string') throw `placeholder must be a string on field "${field.name}"`;
                normalized.placeholder = field.placeholder;
            }
            break;
        case 'checkbox':
            if (!is_non_empty_string(field.label)) {
                throw `checkbox "${field.name}" requires a non-empty "label"`;
            }
            normalized.label = field.label;
            // A checkbox submits its boolean checked state, period — no typed
            // checked/unchecked values in the contract (extras are ignored like
            // any other unknown key, per the forward-compat rule below).
            break;
        case 'hidden':
            // JSON scalars only — the declared type (string/boolean/number) survives to the
            // POST body for metadata/custom_data targets (renderer stamps data-hub-value-type).
            if (field.value === undefined || !is_primitive(field.value)
                || (typeof field.value === 'number' && !Number.isFinite(field.value))) {
                throw `hidden "${field.name}" requires a string/number/boolean "value"`;
            }
            normalized.value = field.value;
            // A required hidden input would invisibly hard-block submit via validateAllRequiredFields.
            if (normalized.required) {
                throw `hidden "${field.name}" cannot be required`;
            }
            break;
        case 'group': {
            if (inside_group) {
                throw 'groups cannot be nested inside groups';
            }
            if (field.variant !== undefined && !FORM_FIELDS_ENUMS.group_variants.includes(field.variant)) {
                throw `invalid group variant "${field.variant}"`;
            }
            normalized.variant = field.variant || 'inner_card';
            if (!Array.isArray(field.children) || field.children.length === 0) {
                throw 'group requires a non-empty flat "children" field array';
            }
            normalized.children = field.children.map(child => validate_field(child, true));
            break;
        }
        case 'submit':
            if (inside_group) {
                throw 'submit is only allowed in top-level rows, not inside a group';
            }
            if (field.text !== undefined) {
                if (!is_non_empty_string(field.text)) throw 'submit "text" must be a non-empty string when set';
                normalized.text = field.text;
            }
            break;
        case 'sms_optin':
            if (inside_group) {
                throw 'sms_optin is only allowed in top-level rows, not inside a group';
            }
            break;
        default:
            break;
    }

    // Unknown EXTRA keys are ignored (forward compat); unknown VALUES of known enum keys hard-fail above.
    return normalized;
};

/**
 * Cross-field rules over the full normalized rows (including group children):
 * no duplicate field names, at most one sms_optin, at most one submit.
 * Throws a string detail message (caught by parse_and_validate_form_fields).
 * @param {Array<Array<Object>>} rows - normalized rows.
 */
const validate_cross_field_rules = (rows) => {
    const seen_names = new Set();
    let sms_optin_count = 0;
    let submit_count = 0;
    const walk_field = (field) => {
        if (field.type === 'sms_optin' && ++sms_optin_count > 1) {
            throw 'only one sms_optin field is allowed per form';
        }
        if (field.type === 'submit' && ++submit_count > 1) {
            throw 'only one submit field is allowed per form';
        }
        if (field.name !== undefined) {
            if (seen_names.has(field.name)) {
                throw `duplicate field name "${field.name}"`;
            }
            seen_names.add(field.name);
        }
        if (Array.isArray(field.children)) {
            field.children.forEach(walk_field);
        }
    };
    rows.forEach(row => row.forEach(walk_field));
};

/**
 * Decode + strictly validate the `data-hub-form-fields` attribute value.
 * @param {string} raw_attr_string - base64(UTF-8 JSON) attribute value.
 * @param {Function} [error_function]
 * @returns {Array<Array<Object>>|null} normalized rows (defaults applied) or null.
 */
export const parse_and_validate_form_fields = (raw_attr_string, error_function = window?.data_hub?.error_function) => {
    if (!raw_attr_string || typeof raw_attr_string !== 'string' || raw_attr_string.trim() === '') {
        return null;
    }
    const decoded = parse_base64_json_utf8(raw_attr_string, error_function);
    if (decoded === null) {
        // Decode error already reported by parse_base64_json_utf8.
        return null;
    }
    try {
        if (!Array.isArray(decoded) || decoded.length === 0) {
            throw 'must be a non-empty array of rows';
        }
        const normalized_rows = decoded.map((row, row_index) => {
            if (!Array.isArray(row) || row.length === 0) {
                throw `row ${row_index} must be a non-empty array of field objects`;
            }
            return row.map(field => validate_field(field, false));
        });
        validate_cross_field_rules(normalized_rows);
        return normalized_rows;
    } catch (detail) {
        if (typeof detail !== 'string') {
            // A real throw (bug), not a validation detail — surface it fully.
            detail = detail?.stack || detail?.message || String(detail);
        }
        error_function?.('data-hub-form-fields invalid: ' + detail);
        return null;
    }
};

/**
 * True when any field (including group children) in validated rows has the given type.
 * Safe on null/undefined rows.
 * @param {Array<Array<Object>>|null} rows
 * @param {string} type
 * @returns {boolean}
 */
export const fields_json_contains_type = (rows, type) => {
    if (!Array.isArray(rows)) return false;
    return rows.some(row => Array.isArray(row) && row.some(field =>
        field?.type === type
        || (field?.type === 'group' && Array.isArray(field.children) && field.children.some(child => child?.type === type))
    ));
};

/**
 * Run every author-facing string (text/label/placeholder/value) through a template
 * function (processHandlebarsTemplate bound to the element's handlebars_context).
 * Mutates the validated rows in place — call once, after parse, before render.
 * @param {Array<Array<Object>>|null} rows
 * @param {Function} template_function - (string) => string
 */
export const apply_template_to_form_fields = (rows, template_function) => {
    if (!Array.isArray(rows) || typeof template_function !== 'function') return rows;
    const template_field = (field) => {
        ['text', 'label', 'placeholder', 'value'].forEach(key => {
            if (typeof field[key] === 'string') {
                field[key] = template_function(field[key]);
            }
        });
        if (Array.isArray(field.children)) {
            field.children.forEach(template_field);
        }
    };
    rows.forEach(row => row.forEach(template_field));
    return rows;
};
